import { useContext, useState, useCallback } from 'react';
import { Button, Table, showDialogConfirm } from '@octano/global-ui';
import { Row, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useFormContext, useFieldArray } from 'react-hook-form';

import { CourseFormContext } from '../../providers/CourseProvider';
import { useColumnsPrerequisites } from '../../hooks/useColumnsPrerequisites';
import { SectionTitle } from '../../../../../components/text';
import { CourseFormFields, CourseFormMode } from '../../types';
import TableEmptyContent from '../../../../../components/text/TableEmptyContent';
import ModalAddPrerequisite from '../modals/ModalAddPrerequisite';
import { TranslationsKeys } from '../../../../../locales/translations';

const CoursePrerequisitesSection = () => {
  const { t } = useTranslation(TranslationsKeys.ACADEMIC_OFFER_COURSES_UPDATE);
  const { control } = useFormContext<CourseFormFields>();

  const { mode } = useContext(CourseFormContext);

  const {
    fields: prerequisites = [],
    remove: removePrerequisite,
  } = useFieldArray({
    control,
    name: 'prerequisites',
  });

  const [modalPrerequisitesOpen, setModalPrerequisitesOpen] = useState<boolean>(
    false,
  );

  const onDeletePrerequisite = useCallback(
    (index: number) => {
      showDialogConfirm({
        icon: {
          name: 'warning',
          color: 'warning',
        },
        title: t(`removePrerequisiteTitle`),
        subtitle: '',
        btnConfirm: {
          text: t(`common:actions.confirm`),
          onConfirm: () => removePrerequisite(index),
        },
        btnClose: {
          text: t(`common:actions.cancel`),
        },
      });
    },
    [removePrerequisite, t],
  );

  const { columns: columnsPrerequisites } = useColumnsPrerequisites({
    mode,
    onDeletePrerequisite,
  });

  return (
    <>
      <Row>
        <Col
          xs={12}
          className="d-flex justify-content-between align-items-center p-4"
        >
          <SectionTitle text={t(`prerequisites`)} />
          {mode !== CourseFormMode.WATCH && (
            <Button
              icon="plus"
              onClick={() => setModalPrerequisitesOpen(true)}
              size="md"
              text={t(`addPrerequisite`)}
            />
          )}
        </Col>
        <Col xs={12}>
          <Table
            columns={columnsPrerequisites}
            data={prerequisites}
            noResultsText={
              <TableEmptyContent
                title={t(`titleEmptyPrerequisites`)}
                subtitle={t(
                  `${
                    mode === CourseFormMode.WATCH
                      ? 'watchBodyEmptyPrerequisites'
                      : 'bodyEmptyPrerequisites'
                  }`,
                )}
              />
            }
          />
        </Col>
      </Row>

      <ModalAddPrerequisite
        isOpen={modalPrerequisitesOpen}
        toggle={() => setModalPrerequisitesOpen(false)}
      />
    </>
  );
};

export default CoursePrerequisitesSection;
